import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, WifiOff, RefreshCw, ListChecks, Bug, LayoutDashboard, Puzzle, Bot,
  Check, X, Clock, DollarSign, Globe, User, Monitor, Repeat, ExternalLink,
  FolderKanban, ChevronRight, ChevronDown, Inbox, Plus, Sparkles,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { StatusBadge, SeverityTag } from "@/components/ui/Badge";
import StepTimeline from "@/components/shared/StepTimeline";
import BlockedDiagnosis from "@/components/shared/BlockedDiagnosis";
import { blockedReasonOfRun } from "@/lib/blockedDiagnosis";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { listCompanyRuns, listCompanyBugs } from "@/api/company";
import { getRun } from "@/api/runs";
import { listExtRuns, getExtRun } from "@/api/extension";
import { listExeRuns, getExeRun } from "@/api/exe";
import { getProjects } from "@/api/projects";
import { runCost, isBug } from "@/lib/flows";
import { DEMO_PROJECTS } from "@/data/projectsMock";
import { formatDateTime } from "@/lib/dates";
import { toastSuccess } from "@/lib/toast";
import { formatUsd } from "@/lib/format";

// ---------------------------------------------------------------------------
// The three testing flows. Each is a fully isolated data source (separate
// backends / tables) but all are company-scoped, so the Admin sees every
// tester's runs. `runsApi` lists; `detailApi` hydrates the detail modal.
// ---------------------------------------------------------------------------
const now = Date.now();
const iso = (hAgo) => new Date(now - hAgo * 3600000).toISOString();


const FLOWS = {
  dashboard: {
    key: "dashboard", label: "Tester Dashboard", icon: LayoutDashboard,
    blurb: "Cloud runs launched by testers from the web app.",
    runsApi: () => listCompanyRuns(200),
    detailApi: getRun,
    // Cloud/headless dashboard testing is paused — this source is shown but inert.
    disabled: true,
    demoRuns: [
      { id: "d1", status: "passed", url: "https://acme.com/login", instruction: "Login happy path", steps_passed: 5, steps_failed: 0, duration_seconds: 12.3, created_at: iso(2), tester: "anita@acme.com" },
      { id: "d2", status: "failed", url: "https://acme.com/cart", instruction: "Checkout with an empty cart", steps_passed: 2, steps_failed: 1, duration_seconds: 8.1, created_at: iso(6), tester: "ben@acme.com" },
      { id: "d3", status: "error", url: "https://acme.com/pay", instruction: "Pay with a test card", steps_passed: 3, steps_failed: 1, duration_seconds: 21.7, created_at: iso(28), tester: "carl@acme.com" },
    ],
  },
  extension: {
    key: "extension", label: "Extension Testing", icon: Puzzle,
    blurb: "Headed runs driven live in a tester's real Chrome tab.",
    runsApi: () => listExtRuns(200),
    detailApi: getExtRun,
    demoRuns: [
      { id: "e1", status: "passed", url: "https://app.acme.com/dashboard", instruction: "Filter the reports table", browser: "chromium", is_replay: false, duration_seconds: 9.4, created_at: iso(3), token_usage: { estimated_cost_usd: 0.021 } },
      { id: "e2", status: "failed", url: "https://app.acme.com/settings", instruction: "Update the billing email", browser: "chromium", is_replay: true, duration_seconds: 14.2, created_at: iso(9), token_usage: { estimated_cost_usd: 0.038 } },
    ],
  },
  exe: {
    key: "exe", label: "Exe Agent Testing", icon: Bot,
    blurb: "Runs executed on a tester's machine by the desktop agent.",
    runsApi: () => listExeRuns(200),
    detailApi: getExeRun,
    demoRuns: [
      { id: "x1", status: "passed", url: "https://internal.acme.com", instruction: "Smoke test the internal portal", browser: "chromium", is_replay: false, duration_seconds: 33.5, created_at: iso(1), token_usage: { estimated_cost_usd: 0.052 } },
      { id: "x2", status: "failed", url: "https://internal.acme.com/orders", instruction: "Create an order end-to-end", browser: "edge", is_replay: false, duration_seconds: 47.9, created_at: iso(20), token_usage: { estimated_cost_usd: 0.081 } },
      { id: "x3", status: "cancelled", url: "https://internal.acme.com/reports", instruction: "Export the monthly report", browser: "chromium", is_replay: true, duration_seconds: 5.0, created_at: iso(52), token_usage: { estimated_cost_usd: 0.004 } },
    ],
  },
};

// Disabled "Tester Dashboard" (cloud runs) sits last, after Exe Agent.
const FLOW_ORDER = ["extension", "exe", "dashboard"];
const STATUS_OPTIONS = ["passed", "failed", "error", "cancelled", "blocked"];

const fmtWhen = (v) => formatDateTime(v);
function fmtDur(s) { return s == null ? "—" : s < 60 ? `${Number(s).toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`; }
// A run that genuinely cost NOTHING is not a run whose cost is unknown. The old
// `c ? ... : null` collapsed both into "—", so every zero-cost replay — which is
// most of them, since a compiled replay calls no model — looked like missing data.
// formatUsd shows $0.00 for free and "—" only when there is no cost to read.
function costOf(r) {
  const usage = r.token_usage;
  if (usage === null || usage === undefined) return null;
  return formatUsd(runCost(usage));
}
function isFail(status) { return ["failed", "error"].includes(String(status).toLowerCase()); }

// Normalize any flow's step list into the shape StepTimeline consumes. Keeps
// status lowercase (StepTimeline upper-cases + aliases it) and passes through the
// richer fields (target / input_value / description / screenshot) when a flow
// provides them, so the admin timeline reads like the tester's.
function normSteps(steps) {
  return (steps || []).map((s, i) => ({
    index: s.step_index ?? s.index ?? i,
    step_index: s.step_index ?? s.index ?? i,
    action: s.action || s.type || "step",
    status: String(s.status || "pass").toLowerCase(),
    reason: s.reason || s.detail || s.thought || s.note || s.description || "",
    target: s.target,
    input_value: s.input_value,
    user_action_description: s.user_action_description,
    screenshot_path: s.screenshot_path,
    screenshot: s.screenshot,
  }));
}
// Normalize any flow's bug into a common shape (or null).
function normBug(d) {
  const b = d.bug || d.bug_report || null;
  if (!b) return null;
  return {
    title: b.title || "Issue found",
    severity: b.severity || "medium",
    description: b.description || "",
    expected: b.expected || "",
    actual: b.actual || "",
    steps_to_reproduce: b.steps_to_reproduce || b.repro || [],
  };
}

function MetaChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-white/5 dark:text-slate-300">
      <Icon size={13} /> {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — fetches the full run for the active flow, then renders meta +
// step timeline + a structured bug report.
// ---------------------------------------------------------------------------
function DetailModal({ flow, row, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(false); setDetail(null);
    FLOWS[flow].detailApi(row.id)
      .then((d) => { if (alive) setDetail(d); })
      .catch(() => { if (alive) setErr(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [flow, row.id]);

  // Fall back to the list row when detail can't be fetched (offline/demo).
  const d = detail || row;
  // NO synthesized evidence. When the detail fetch failed this used to substitute
  // DEMO_STEPS/DEMO_BUG — four hardcoded steps ("Opened the login page"…) and an
  // invented bug report — rendered under the real "Step timeline" heading with a
  // real "3 passed" counter. An admin reviewing a genuinely failed case was shown
  // a fabricated run. The tester-side copy of this modal already refuses to do it.
  const steps = normSteps(d.step_results || d.steps);
  const bug = normBug(d);
  const isBlocked = String(d.status || "").toLowerCase() === "blocked";
  const blockedReason = useMemo(() => (isBlocked ? blockedReasonOfRun(d) : ""), [isBlocked, d]);
  const accountable = isBlocked ? steps.filter((s) => s.action !== "done") : steps;
  const passed = accountable.filter((s) => s.status === "pass").length;
  const failed = accountable.filter((s) => s.status === "fail" || s.status === "error").length;
  const executedSteps = accountable.filter((s) => s.action !== "done").length;
  const cost = costOf(d);

  return (
    <Modal open onClose={onClose} title="Run detail" size="xl">
      {loading && !detail ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <div className="space-y-5">
          {err && (
            <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
              <WifiOff size={14} className="mt-0.5 shrink-0" /> Couldn&apos;t load this run&apos;s detail. Nothing below is
              from the run itself — close and try again.
            </div>
          )}

          {/* meta */}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={d.status} />
            {d.tester && <MetaChip icon={User}>{d.tester}</MetaChip>}
            {(d.browser) && <MetaChip icon={Monitor}>{d.browser}</MetaChip>}
            {d.is_replay && <MetaChip icon={Repeat}>replay</MetaChip>}
            <MetaChip icon={Clock}>{fmtDur(d.duration_seconds)}</MetaChip>
            {cost && <MetaChip icon={DollarSign}>{cost.replace("$", "")}</MetaChip>}
            <MetaChip icon={Clock}>{fmtWhen(d.created_at)}</MetaChip>
          </div>

          <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-white/5">
            <p className="text-sm text-slate-700 dark:text-slate-200">“{d.instruction}”</p>
            <a href={d.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline dark:text-brand-400">
              <Globe size={12} /> {d.url} <ExternalLink size={11} />
            </a>
          </div>

          {isBlocked && (
            <BlockedDiagnosis
              reason={blockedReason}
              affected={executedSteps > 0
                ? `Execution stopped at the blocking point after ${executedSteps} step${executedSteps === 1 ? "" : "s"} ran; no result was produced.`
                : null}
              missingEvidence={!Array.isArray(steps) || steps.filter((s) => s.screenshot_path || s.screenshot).length === 0}
            />
          )}

          {/* step counters */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-lg bg-slate-100 px-2.5 py-1 font-medium text-slate-600 dark:bg-white/5 dark:text-slate-300">{steps.length} steps</span>
            <span className="rounded-lg bg-emerald-100 px-2.5 py-1 font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">{passed} passed</span>
            {failed > 0 && <span className="rounded-lg bg-rose-100 px-2.5 py-1 font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">{failed} failed</span>}
          </div>

          {/* bug report */}
          {bug && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 dark:border-rose-500/20 dark:bg-rose-500/10">
              <div className="flex items-start justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white"><Bug size={15} className="text-rose-500" /> {bug.title}</span>
                <SeverityTag severity={bug.severity} />
              </div>
              {bug.description && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{bug.description}</p>}
              <dl className="mt-3 space-y-1.5 text-xs">
                {bug.expected && <div><dt className="inline font-semibold text-slate-500 dark:text-slate-400">Expected: </dt><dd className="inline text-slate-600 dark:text-slate-300">{bug.expected}</dd></div>}
                {bug.actual && <div><dt className="inline font-semibold text-slate-500 dark:text-slate-400">Actual: </dt><dd className="inline text-slate-600 dark:text-slate-300">{bug.actual}</dd></div>}
              </dl>
              {Array.isArray(bug.steps_to_reproduce) && bug.steps_to_reproduce.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Steps to reproduce</p>
                  <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-xs text-slate-600 dark:text-slate-300">
                    {bug.steps_to_reproduce.map((s, i) => <li key={i}>{typeof s === "string" ? s : s.text || JSON.stringify(s)}</li>)}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* timeline — same rich developer-readable cards as the tester
              dashboard. runId is passed only for the dashboard flow, whose
              screenshots load from the /test/runs/{id}/screenshots endpoint;
              extension/exe steps carry their shot inline (step.screenshot). */}
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Step timeline</h4>
            {steps.length === 0
              ? (
                <p className="text-sm text-slate-400">
                  {err ? "Couldn't load the steps for this run." : "No step detail recorded for this run."}
                </p>
              )
              /* runId + kind for EVERY flow. Passing runId only for "dashboard" —
                 the one disabled flow — meant screenshots could never load on
                 extension or exe, the two reachable ones. */
              : <StepTimeline steps={steps} runId={d.id} kind={flow === "dashboard" ? "cloud" : flow} runStatus={d.status} />}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// One flow's panel: stat strip + Runs/Bugs sub-tabs + filtered, paged table.
// ---------------------------------------------------------------------------
function FlowPanel({ flow, onOpen }) {
  const cfg = FLOWS[flow];
  const [view, setView] = useState("runs"); // runs | bugs

  const runsState = useApiData(cfg.runsApi, cfg.demoRuns);
  // Bugs for the dashboard flow come from a dedicated endpoint; for extension /
  // exe there's no separate bug list, so we surface failed runs as "bugs".
  const dashBugsFetch = flow === "dashboard" ? () => listCompanyBugs(200) : () => Promise.resolve([]);
  const dashBugs = useApiData(dashBugsFetch, FLOWS.dashboard.demoRuns.filter((r) => isFail(r.status)).map((r) => ({ id: r.id, run_id: r.id, title: r.instruction, severity: "high", url: r.url, created_at: r.created_at })));

  const runs = useMemo(() => (Array.isArray(runsState.data) ? runsState.data : []), [runsState.data]);
  const demo = runsState.demo || (flow === "dashboard" && view === "bugs" && dashBugs.demo);
  const loading = runsState.loading || (flow === "dashboard" && view === "bugs" && dashBugs.loading);

  const stats = useMemo(() => {
    const passed = runs.filter((r) => String(r.status).toLowerCase() === "passed").length;
    const failed = runs.filter((r) => isFail(r.status)).length;
    return { total: runs.length, passed, failed };
  }, [runs]);

  // A BUG is a run that produced a bug report — not merely one that failed.
  // These two were the same number for the headed runners (`stats.failed`), so
  // the Bugs card and the Failures card beside it always agreed, which made one
  // of them noise. `has_bug` comes from the run list; older payloads without it
  // fall back to the failed status so nothing silently reads zero.
  const bugsRows = flow === "dashboard"
    ? (Array.isArray(dashBugs.data) ? dashBugs.data : [])
    : runs.filter(isBug);
  const bugCount = bugsRows.length;

  const statCards = [
    { label: "Total runs", value: stats.total, icon: ListChecks, cls: "text-brand-600 bg-brand-500/10 dark:text-brand-300" },
    { label: "Passed", value: stats.passed, icon: Check, cls: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300" },
    { label: "Failures", value: stats.failed, icon: X, cls: "text-rose-600 bg-rose-500/10 dark:text-rose-300" },
    { label: "Bugs", value: bugCount, icon: Bug, cls: "text-amber-600 bg-amber-500/10 dark:text-amber-300" },
  ];

  // Column sets differ per flow (dashboard has tester + step counts; the headed
  // flows expose browser + replay instead).
  const testCol = {
    key: "instruction", header: "Test",
    render: (r) => (
      <div className="min-w-0 max-w-[15rem] sm:max-w-[22rem]">
        <p className="truncate font-medium text-slate-800 dark:text-white">{r.instruction || r.title || "—"}</p>
        <p className="truncate text-xs text-slate-400">{r.url}</p>
      </div>
    ),
  };
  const statusCol = { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> };
  const whenCol = { key: "created_at", header: "When", sortable: true, align: "right", render: (r) => <span className="whitespace-nowrap text-xs text-slate-400">{fmtWhen(r.created_at)}</span> };
  const durCol = { key: "duration_seconds", header: "Duration", sortable: true, align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-slate-400">{fmtDur(r.duration_seconds)}</span> };

  const runColumns = flow === "dashboard"
    ? [
        testCol,
        { key: "tester", header: "Tester", render: (r) => <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{r.tester || "—"}</span> },
        statusCol,
        { key: "steps", header: "Steps", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums"><span className="text-emerald-600 dark:text-emerald-400">{r.steps_passed ?? 0}✓</span> <span className="text-rose-500">{r.steps_failed ?? 0}✗</span></span> },
        durCol, whenCol,
      ]
    : [
        testCol,
        { key: "browser", header: "Browser", render: (r) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
            <Monitor size={12} /> {r.browser || "—"}{r.is_replay && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-300">replay</span>}
          </span>
        ) },
        statusCol,
        { key: "cost", header: "Cost", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-slate-400">{costOf(r) || "—"}</span> },
        durCol, whenCol,
      ];

  const bugColumns = flow === "dashboard"
    ? [
        { key: "title", header: "Bug", render: (b) => (
          <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]"><p className="truncate font-medium text-slate-800 dark:text-white">{b.title}</p><p className="truncate text-xs text-slate-400">{b.url}</p></div>
        ) },
        { key: "severity", header: "Severity", render: (b) => <SeverityTag severity={b.severity} /> },
        whenCol,
      ]
    : [
        testCol,
        { key: "browser", header: "Browser", render: (r) => <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{r.browser || "—"}</span> },
        statusCol,
        durCol, whenCol,
      ];

  const rowsForView = view === "runs" ? runs : bugsRows;
  const columnsForView = view === "runs" ? runColumns : bugColumns;
  // Clicking a dashboard bug opens its run; extension/exe bug rows ARE runs.
  const openRow = (row) => onOpen(flow, view === "bugs" && flow === "dashboard" ? { ...row, id: row.run_id || row.id } : row);

  return (
    <div>
      {/* stat strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((s) => (
          <div key={s.label} className="card flex items-center gap-3 p-4">
            <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", s.cls)}><s.icon size={18} /></span>
            <div className="min-w-0">
              <p className="truncate text-xs text-slate-400">{s.label}</p>
              <p className="font-display text-xl font-bold text-slate-900 dark:text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Runs / Bugs sub-tabs */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
          {[["runs", "Runs", ListChecks, stats.total], ["bugs", "Bugs", Bug, bugCount]].map(([key, label, Icon, count]) => (
            <button key={key} onClick={() => setView(key)}
              className={cn("inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors",
                view === key ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
              <Icon size={15} /> {label} <span className="opacity-70">({count})</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400">{cfg.blurb}</p>
      </div>

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : (
        <DataTable
          columns={columnsForView}
          rows={rowsForView}
          getRowId={(r) => r.id}
          onRowClick={openRow}
          searchKeys={["instruction", "url", "tester", "title"]}
          filters={view === "runs" ? [{ key: "status", label: "Status", options: STATUS_OPTIONS }] : []}
          initialSort={{ key: "created_at", dir: "desc" }}
          maxHeight="36rem"
          dateKey="created_at"
          dateLabel="Run date"
          pageSize={12}
          emptyLabel={view === "runs" ? "No runs in this flow yet." : "No bugs — nice."}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatPill — compact inline stat used in project section headers.
// ---------------------------------------------------------------------------
function StatPill({ icon: Icon, value, label, tone }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/70 bg-white/70 px-2.5 py-1 text-xs backdrop-blur dark:border-white/10 dark:bg-white/5">
      <Icon size={12} className={tone} />
      <span className="font-semibold text-slate-900 dark:text-white">{value}</span>
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// CompanyProjectSection — one collapsible project card for the company
// Tester Dashboard flow. Mirrors the tester's ProjectSection but uses the
// company-scoped data shapes (raw snake_case fields from /company/runs).
// ---------------------------------------------------------------------------
function CompanyProjectSection({ project, runs, bugs, onOpen }) {
  const [expanded, setExpanded] = useState(true);
  const [view, setView] = useState("runs");

  const passed = runs.filter((r) => String(r.status).toLowerCase() === "passed").length;
  const failed = runs.filter((r) => isFail(r.status)).length;
  const openBugs = bugs.length;

  const whenCol = {
    key: "created_at", header: "When", align: "right",
    render: (r) => <span className="whitespace-nowrap text-xs text-slate-400">{fmtWhen(r.created_at)}</span>,
  };
  const testCol = {
    key: "instruction", header: "Test",
    render: (r) => (
      <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]">
        <p className="truncate font-medium text-slate-800 dark:text-white">{r.instruction || "—"}</p>
        <p className="truncate text-xs text-slate-400">{r.url}</p>
      </div>
    ),
  };

  const runColumns = [
    testCol,
    { key: "tester", header: "Tester", render: (r) => <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{r.tester || "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "steps", header: "Steps", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums"><span className="text-emerald-600 dark:text-emerald-400">{r.steps_passed ?? 0}✓</span> <span className="text-rose-500">{r.steps_failed ?? 0}✗</span></span> },
    { key: "duration_seconds", header: "Duration", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-slate-400">{fmtDur(r.duration_seconds)}</span> },
    whenCol,
  ];

  const bugColumns = [
    { key: "title", header: "Bug", render: (b) => (
      <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]"><p className="truncate font-medium text-slate-800 dark:text-white">{b.title}</p><p className="truncate text-xs text-slate-400">{b.url}</p></div>
    ) },
    { key: "severity", header: "Severity", render: (b) => <SeverityTag severity={b.severity} /> },
    whenCol,
  ];

  const rowsForView = view === "runs" ? runs : bugs;
  const columnsForView = view === "runs" ? runColumns : bugColumns;
  const openRow = (row) => onOpen("dashboard", view === "bugs" ? { ...row, id: row.run_id } : row);

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-soft">
          <FolderKanban size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-display text-base font-semibold text-slate-900 dark:text-white">{project.name}</h3>
            {project.base_url && (
              <span className="inline-flex items-center gap-1 truncate rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.6875rem] text-slate-500 dark:bg-white/5 dark:text-slate-400">
                <Globe size={11} /> <span className="truncate max-w-[16rem]">{project.base_url}</span>
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <StatPill icon={ListChecks} value={runs.length} label="runs" tone="text-brand-500" />
            <StatPill icon={Check} value={passed} label="passed" tone="text-emerald-500" />
            <StatPill icon={X} value={failed} label="failed" tone="text-rose-500" />
            <StatPill icon={Bug} value={openBugs} label="bugs" tone="text-amber-500" />
          </div>
        </div>
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-transform", expanded && "rotate-90 text-brand-500")}>
          <ChevronRight size={16} />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-200 dark:border-white/10"
          >
            <div className="px-4 pb-4 pt-3">
              <div className="mb-3 inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
                {[["runs", "Runs", ListChecks, runs.length], ["bugs", "Bugs", Bug, bugs.length]].map(([key, label, Icon, count]) => (
                  <button key={key} onClick={() => setView(key)}
                    className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                      view === key ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
                    <Icon size={13} /> {label} <span className="opacity-70">({count})</span>
                  </button>
                ))}
              </div>

              <DataTable
                columns={columnsForView}
                rows={rowsForView}
                getRowId={(r) => r.id}
                searchKeys={["instruction", "url", "title"]}
                filters={view === "runs"
                  ? [{ key: "status", label: "Status", options: ["passed", "failed", "error", "cancelled", "blocked"] }]
                  : []}
                onRowClick={openRow}
                maxHeight="24rem"
                pageSize={8}
                emptyLabel={view === "runs" ? "No runs in this project yet." : "No bugs — nice."}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompanyUnscopedSection — runs/bugs with no project_id. Hidden when empty.
// ---------------------------------------------------------------------------
function CompanyUnscopedSection({ runs, bugs, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState("runs");

  const passed = runs.filter((r) => String(r.status).toLowerCase() === "passed").length;
  const failed = runs.filter((r) => isFail(r.status)).length;
  const empty = runs.length === 0 && bugs.length === 0;
  if (empty) return null;

  const whenCol = {
    key: "created_at", header: "When", align: "right",
    render: (r) => <span className="whitespace-nowrap text-xs text-slate-400">{fmtWhen(r.created_at)}</span>,
  };
  const testCol = {
    key: "instruction", header: "Test",
    render: (r) => (
      <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]">
        <p className="truncate font-medium text-slate-800 dark:text-white">{r.instruction || "—"}</p>
        <p className="truncate text-xs text-slate-400">{r.url}</p>
      </div>
    ),
  };

  const runColumns = [
    testCol,
    { key: "tester", header: "Tester", render: (r) => <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{r.tester || "—"}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "duration_seconds", header: "Duration", align: "right", render: (r) => <span className="whitespace-nowrap text-xs tabular-nums text-slate-500 dark:text-slate-400">{fmtDur(r.duration_seconds)}</span> },
    whenCol,
  ];

  const bugColumns = [
    { key: "title", header: "Bug", render: (b) => (
      <div className="min-w-0 max-w-[15rem] sm:max-w-[24rem]"><p className="truncate font-medium text-slate-800 dark:text-white">{b.title}</p><p className="truncate text-xs text-slate-400">{b.url}</p></div>
    ) },
    { key: "severity", header: "Severity", render: (b) => <SeverityTag severity={b.severity} /> },
    whenCol,
  ];

  const rowsForView = view === "runs" ? runs : bugs;
  const columnsForView = view === "runs" ? runColumns : bugColumns;
  const openRow = (row) => onOpen("dashboard", view === "bugs" ? { ...row, id: row.run_id } : row);

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
      >
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 shadow-soft dark:bg-white/10 dark:text-slate-300">
          <Inbox size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-display text-base font-semibold text-slate-900 dark:text-white">Unscoped</h3>
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.6875rem] text-slate-500 dark:bg-white/5 dark:text-slate-400">
              runs with no project attached
            </span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <StatPill icon={ListChecks} value={runs.length} label="runs" tone="text-brand-500" />
            <StatPill icon={Check} value={passed} label="passed" tone="text-emerald-500" />
            <StatPill icon={X} value={failed} label="failed" tone="text-rose-500" />
            <StatPill icon={Bug} value={bugs.length} label="bugs" tone="text-amber-500" />
          </div>
        </div>
        <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-transform", expanded && "rotate-90 text-brand-500")}>
          <ChevronRight size={16} />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-200 dark:border-white/10"
          >
            <div className="px-4 pb-4 pt-3">
              <div className="mb-3 inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
                {[["runs", "Runs", ListChecks, runs.length], ["bugs", "Bugs", Bug, bugs.length]].map(([key, label, Icon, count]) => (
                  <button key={key} onClick={() => setView(key)}
                    className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
                      view === key ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
                    <Icon size={13} /> {label} <span className="opacity-70">({count})</span>
                  </button>
                ))}
              </div>

              <DataTable
                columns={columnsForView}
                rows={rowsForView}
                getRowId={(r) => r.id}
                searchKeys={["instruction", "url", "title"]}
                filters={view === "runs"
                  ? [{ key: "status", label: "Status", options: ["passed", "failed", "error", "cancelled", "blocked"] }]
                  : []}
                onRowClick={openRow}
                maxHeight="24rem"
                pageSize={8}
                emptyLabel={view === "runs" ? "No unscoped runs." : "No unscoped bugs."}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DashboardProjectGroup — project-grouped view for the Tester Dashboard flow.
// Loads projects + all company runs/bugs, groups by project, and renders
// collapsible sections (same pattern as the tester's grouped Runs & Bugs).
// ---------------------------------------------------------------------------
function DashboardProjectGroup({ onOpen, refreshNonce }) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsDemo, setProjectsDemo] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const runsState = useApiData(() => listCompanyRuns(200), FLOWS.dashboard.demoRuns);
  const bugsState = useApiData(() => listCompanyBugs(200), []);

  const loading = runsState.loading || bugsState.loading || projectsLoading;
  const demo = runsState.demo || bugsState.demo || projectsDemo;

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const data = await getProjects();
      setProjects(data || []);
      setProjectsDemo(false);
    } catch (e) {
      setProjects(DEMO_PROJECTS);
      setProjectsDemo(true);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (projects.length === 0) return;
    setExpanded((prev) => {
      if (prev.size > 0) return prev;
      return new Set(projects.slice(0, 3).map((p) => p.id));
    });
  }, [projects]);

  const runsData = runsState.data;
  const bugsData = bugsState.data;

  const { runsByProject, unscopedRuns, totals } = useMemo(() => {
    const data = Array.isArray(runsData) ? runsData : [];
    const byProj = {};
    const unscoped = [];
    let passed = 0, failed = 0;
    for (const r of data) {
      if (r.project_id) {
        (byProj[r.project_id] = byProj[r.project_id] || []).push(r);
      } else {
        unscoped.push(r);
      }
      if (String(r.status).toLowerCase() === "passed") passed++;
      else if (isFail(r.status)) failed++;
    }
    return {
      runsByProject: byProj,
      unscopedRuns: unscoped,
      totals: { total: data.length, passed, failed },
    };
  }, [runsData]);

  const { bugsByProject, unscopedBugs } = useMemo(() => {
    const data = Array.isArray(bugsData) ? bugsData : [];
    const byProj = {};
    const unscoped = [];
    for (const b of data) {
      if (b.project_id) {
        (byProj[b.project_id] = byProj[b.project_id] || []).push(b);
      } else {
        unscoped.push(b);
      }
    }
    return { bugsByProject: byProj, unscopedBugs: unscoped };
  }, [bugsData]);

  const anyExpanded = expanded.size > 0;
  const bugsCount = Array.isArray(bugsData) ? bugsData.length : 0;

  if (loading && projects.length === 0 && (!Array.isArray(runsData) || runsData.length === 0)) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 size={26} className="animate-spin" />
      </div>
    );
  }

  if (!projectsDemo && projects.length === 0 && !loading) {
    return (
      <div className="card flex flex-col items-center gap-3 p-10 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-white shadow-glow">
          <FolderKanban size={22} />
        </span>
        <h2 className="font-display text-lg font-semibold text-slate-900 dark:text-white">No projects yet</h2>
        <p className="max-w-md text-sm text-slate-500 dark:text-slate-400">
          Create a project first — runs and bugs are scoped per project so each project&apos;s results stay isolated.
        </p>
        {/* /dashboard/new is not a registered route — that button 404'd. And
            Button renders its own Link when given `to`; a <button> inside an <a>
            is invalid HTML. */}
        <div className="flex flex-wrap justify-center gap-2">
          <Button to="/admin-co/projects" size="sm"><Plus size={15} /> Create a project</Button>
          <Button to="/admin-co/analytics" size="sm" variant="outline"><Sparkles size={15} /> View analytics</Button>
        </div>
      </div>
    );
  }

  return (
    <div key={refreshNonce}>
      {/* Stat strip */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Total runs", value: totals.total, icon: ListChecks, cls: "text-brand-600 bg-brand-500/10 dark:text-brand-300" },
          { label: "Passed", value: totals.passed, icon: Check, cls: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-300" },
          { label: "Failures", value: totals.failed, icon: X, cls: "text-rose-600 bg-rose-500/10 dark:text-rose-300" },
          { label: "Bugs", value: bugsCount, icon: Bug, cls: "text-amber-600 bg-amber-500/10 dark:text-amber-300" },
        ].map((s) => (
          <div key={s.label} className="card flex items-center gap-3 p-4">
            <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", s.cls)}><s.icon size={18} /></span>
            <div className="min-w-0">
              <p className="truncate text-xs text-slate-400">{s.label}</p>
              <p className="font-display text-xl font-bold text-slate-900 dark:text-white">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Expand / Collapse control */}
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => {
          if (anyExpanded) { setExpanded(new Set()); }
          else { setExpanded(new Set(projects.map((p) => p.id))); }
        }} disabled={projects.length === 0}>
          <ChevronDown size={16} className={cn("transition-transform", anyExpanded && "rotate-180")} />
          {anyExpanded ? "Collapse all" : "Expand all"}
        </Button>
      </div>

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      <div className="space-y-4">
        {projects.map((p) => (
          <CompanyProjectSection
            key={p.id}
            project={p}
            runs={runsByProject[p.id] || []}
            bugs={bugsByProject[p.id] || []}
            onOpen={onOpen}
            defaultExpanded={expanded.has(p.id)}
          />
        ))}
        <CompanyUnscopedSection
          runs={unscopedRuns}
          bugs={unscopedBugs}
          onOpen={onOpen}
        />
      </div>
    </div>
  );
}

export default function RunsBugs() {
  const [params, setParams] = useSearchParams();
  // Deep-link support: /admin-co/activity?flow=extension opens that flow directly
  // (the Overview flow cards link here).
  // Default to the first ENABLED flow; a disabled source (e.g. paused cloud runs)
  // can't be selected via the tab or a ?flow= deep link.
  const firstEnabledFlow = FLOW_ORDER.find((k) => !FLOWS[k].disabled) || FLOW_ORDER[0];
  const requestedFlow = params.get("flow");
  const initialFlow =
    FLOW_ORDER.includes(requestedFlow) && !FLOWS[requestedFlow].disabled
      ? requestedFlow
      : firstEnabledFlow;
  const [flow, setFlow] = useState(initialFlow);
  const [detail, setDetail] = useState(null); // { flow, row }
  const [nonce, setNonce] = useState(0); // force-remount on refresh

  // Refresh remounts the panels (that IS the reload), so there is no promise to
  // await — but a silent remount that returns the same numbers is indistinguishable
  // from a dead button, which is exactly how it was reported. Spin briefly, then say
  // it happened.
  const [refreshing, setRefreshing] = useState(false);
  function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    setNonce((n) => n + 1);
    window.setTimeout(() => {
      setRefreshing(false);
      toastSuccess("Runs & bugs refreshed.");
    }, 600);
  }

  const selectFlow = (key) => {
    if (FLOWS[key].disabled) return; // paused source — not selectable
    setFlow(key);
    setParams(key === firstEnabledFlow ? {} : { flow: key }, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Runs & Bugs"
        subtitle="Every test run and bug across your three testing flows."
        actions={
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
            <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      {/* Flow tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {FLOW_ORDER.map((key) => {
          const f = FLOWS[key];
          const active = flow === key;
          const disabled = !!f.disabled;
          return (
            <button key={key} onClick={() => selectFlow(key)} disabled={disabled}
              title={disabled ? "Temporarily disabled" : undefined}
              aria-disabled={disabled}
              className={cn("group inline-flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-left transition-colors",
                disabled
                  ? "cursor-not-allowed border-slate-200 opacity-60 dark:border-white/10"
                  : active
                  ? "border-brand-500 bg-brand-50/70 dark:border-brand-500/40 dark:bg-brand-500/10"
                  : "border-slate-200 hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20")}>
              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors",
                disabled ? "bg-slate-100 text-slate-300 dark:bg-white/5 dark:text-slate-600"
                : active ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500 group-hover:text-brand-600 dark:bg-white/10 dark:text-slate-300")}>
                <f.icon size={18} />
              </span>
              <span className="min-w-0">
                <span className={cn("flex items-center gap-2 text-sm font-semibold",
                  disabled ? "text-slate-400 dark:text-slate-500" : active ? "text-brand-700 dark:text-brand-200" : "text-slate-700 dark:text-slate-200")}>
                  {f.label}
                  {disabled && <span className="text-[0.625rem] font-semibold lowercase text-rose-500 dark:text-rose-400">temporarily disabled</span>}
                </span>
                <span className="hidden text-xs text-slate-400 sm:block">{f.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>

      {flow === "dashboard"
        ? <DashboardProjectGroup key={`dash-${nonce}`} onOpen={(fl, row) => setDetail({ flow: fl, row })} refreshNonce={nonce} />
        : <FlowPanel key={`${flow}-${nonce}`} flow={flow} onOpen={(fl, row) => setDetail({ flow: fl, row })} />
      }

      {detail && <DetailModal flow={detail.flow} row={detail.row} onClose={() => setDetail(null)} />}
    </>
  );
}
